import React from "react";
import {
  Sparkles,
  ArrowRight,
  CalendarCheck,
  Check,
  Calendar,
  Users,
  Clock,
  Star,
  TrendingUp,
  PlayCircle,
} from "lucide-react";

/**
 * Three ambitious 2026-era visual directions for SlotBook.
 * Each renders a slice of the new hero (with phone + browser combo),
 * a feature/ROI strip, pricing, and a CTA — so you can judge like-for-like.
 *
 * Route: /mockups
 */

const SCHEMES = [
  {
    id: "pangram-editorial",
    name: "Pangram Editorial",
    blurb:
      "Brutalist editorial. Bone cream + carbon black + a single neon-citrus accent. Massive type. Magazine-grade restraint.",
    benchmark: "Aesop · Linear · Cluely · Pangram type foundry",
    pageBg: "#F2EFE7",
    surface: "#FFFFFF",
    surfaceWarm: "#E9E5D9",
    border: "#D7D2C3",
    fg: "#0B0B0B",
    fgMuted: "#5C5A52",
    primary: "#0B0B0B",
    primaryFg: "#F2EFE7",
    accent: "#D8FF3D", // neon citrus
    accentFg: "#0B0B0B",
    success: "#1F8B59",
    headingFont:
      '"Editorial New","Cormorant Garamond","Times New Roman",serif',
    bodyFont: '"Inter","Helvetica Neue",system-ui,sans-serif',
    monoFont: '"JetBrains Mono","Space Mono",ui-monospace,monospace',
    radius: "4px",
    heroBg: "#F2EFE7",
    heroOverlay:
      "radial-gradient(ellipse at 80% -10%, rgba(216,255,61,0.25), transparent 50%)",
  },
  {
    id: "iridescent-noir",
    name: "Iridescent Noir",
    blurb:
      "Dark glassmorphism with an animated iridescent mesh. Cinematic, tech-forward, premium dark mode.",
    benchmark: "Apple Vision Pro · Arc Browser · Vercel · Linear dark",
    pageBg: "#07070C",
    surface: "rgba(255,255,255,0.04)",
    surfaceWarm: "rgba(255,255,255,0.06)",
    border: "rgba(255,255,255,0.08)",
    fg: "#F5F4F2",
    fgMuted: "#9C9AA8",
    primary: "#FFFFFF",
    primaryFg: "#07070C",
    accent: "#A78BFA", // iridescent violet
    accentFg: "#07070C",
    success: "#34D399",
    headingFont: '"Cabinet Grotesk","Inter",system-ui,sans-serif',
    bodyFont: '"Inter","Helvetica Neue",system-ui,sans-serif',
    monoFont: '"JetBrains Mono",ui-monospace,monospace',
    radius: "18px",
    heroBg:
      "radial-gradient(at 20% 30%, rgba(167,139,250,0.35) 0%, transparent 45%), radial-gradient(at 80% 70%, rgba(56,189,248,0.28) 0%, transparent 45%), radial-gradient(at 60% 10%, rgba(244,114,182,0.22) 0%, transparent 50%), #07070C",
    heroOverlay: "radial-gradient(ellipse at 50% 0%, transparent 30%, #07070C 90%)",
  },
  {
    id: "sunset-bondi",
    name: "Sunset Bondi",
    blurb:
      "Sun-warmed mesh gradient hero (coral → magenta → amber) with cream sections. Emotional, distinctly Australian, beauty-forward.",
    benchmark: "Loom · Bondi Sands · MECCA · Arc Browser landing",
    pageBg: "#FFF8F1",
    surface: "#FFFFFF",
    surfaceWarm: "#FFEDD9",
    border: "#F2DCC4",
    fg: "#1A0F12",
    fgMuted: "#7A5F5F",
    primary: "#1A0F12",
    primaryFg: "#FFF8F1",
    accent: "#FF5C5C",
    accentFg: "#FFFFFF",
    success: "#1F8B59",
    headingFont:
      '"Cabinet Grotesk","Outfit","Pangram Sans",system-ui,sans-serif',
    bodyFont: '"Inter","Helvetica Neue",system-ui,sans-serif',
    monoFont: '"Space Grotesk",ui-monospace,monospace',
    radius: "20px",
    heroBg:
      "linear-gradient(120deg, #FFB36B 0%, #FF6B6B 35%, #C44569 65%, #6C5B7B 100%)",
    heroOverlay:
      "radial-gradient(circle at 70% 30%, rgba(255,200,100,0.5), transparent 55%)",
    heroOnDark: true, // hero text reads on the gradient, not on bg
  },
];

/* ---------- HERO PREVIEW with phone + browser combo ---------- */

const PhoneTile = ({ s }) => {
  const isDark = s.id === "iridescent-noir";
  return (
    <div
      className="relative w-[200px] sm:w-[210px] flex-shrink-0"
      style={{
        transform: "rotate(-4deg) translateY(20px)",
      }}
    >
      <div
        className="relative rounded-[2rem] border p-2.5 shadow-2xl"
        style={{
          background: isDark ? "#0A0A10" : "#0F0F11",
          borderColor: isDark ? "#1A1A22" : "#202024",
          boxShadow: `0 30px 60px -20px ${s.accent}40, 0 24px 48px -22px rgba(0,0,0,0.5)`,
        }}
      >
        <div
          className="absolute left-1/2 top-2.5 z-10 h-3 w-12 -translate-x-1/2 rounded-full"
          style={{ background: "#000" }}
        />
        <div
          className="rounded-[1.5rem] overflow-hidden"
          style={{ background: s.pageBg }}
        >
          <div
            className="px-4 pt-3 pb-1.5 flex justify-between text-[9px]"
            style={{ color: s.fgMuted }}
          >
            <span>9:41</span>
            <span style={{ color: s.fg, fontWeight: 600 }}>SlotBook</span>
            <span>100%</span>
          </div>
          <div className="px-3 pb-3">
            <p
              className="mt-2 mb-2 text-[8px] font-bold uppercase tracking-[0.15em]"
              style={{
                color: s.fgMuted,
                fontFamily: s.monoFont,
              }}
            >
              Tuesday, 14 May
            </p>
            <div className="grid grid-cols-3 gap-1.5">
              {["9:30", "10:15", "11:00", "1:30", "2:15", "3:00"].map((t, i) => (
                <div
                  key={t}
                  className="text-center py-1.5 text-[10px] font-semibold"
                  style={
                    i === 2
                      ? {
                          background: s.accent,
                          color: s.accentFg,
                          borderRadius: s.radius,
                          boxShadow: `0 6px 14px -3px ${s.accent}77`,
                        }
                      : {
                          background: s.surface,
                          color: s.fg,
                          border: `1px solid ${s.border}`,
                          borderRadius: s.radius,
                        }
                  }
                >
                  {t}
                </div>
              ))}
            </div>
            <div
              className="mt-3 p-2 text-[9px]"
              style={{
                background: s.surfaceWarm,
                color: s.fgMuted,
                borderRadius: s.radius,
              }}
            >
              <span style={{ color: s.fg, fontWeight: 600 }}>Signature Cut</span>{" "}
              with Amara · 45 min
            </div>
            <div
              className="mt-3 py-2 text-center text-[10px] font-bold"
              style={{
                background: s.primary,
                color: s.primaryFg,
                borderRadius: "999px",
              }}
            >
              Confirm booking
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const BrowserTile = ({ s }) => {
  const onDark = s.id === "iridescent-noir";
  return (
    <div
      className="relative w-full max-w-[440px] flex-shrink-0"
      style={{ transform: "rotate(2deg)" }}
    >
      <div
        className="rounded-2xl overflow-hidden shadow-2xl border"
        style={{
          borderColor: onDark ? "rgba(255,255,255,0.1)" : s.border,
          background: onDark ? "#0A0A10" : s.surface,
          boxShadow: `0 36px 80px -24px ${s.accent}55, 0 24px 60px -24px rgba(0,0,0,0.45)`,
        }}
      >
        {/* window chrome */}
        <div
          className="flex items-center gap-1.5 px-3 py-2 border-b"
          style={{
            background: onDark ? "#101018" : s.surfaceWarm,
            borderColor: onDark ? "rgba(255,255,255,0.06)" : s.border,
          }}
        >
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#FF5F57" }} />
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#FEBC2E" }} />
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#28C840" }} />
          <span
            className="ml-3 text-[10px] font-mono px-2 py-0.5 rounded"
            style={{
              fontFamily: s.monoFont,
              background: onDark ? "rgba(255,255,255,0.06)" : "#FFFFFF",
              color: s.fgMuted,
            }}
          >
            app.slotbook.com.au/calendar
          </span>
        </div>
        {/* dashboard content */}
        <div
          className="p-3"
          style={{ background: onDark ? "#0A0A10" : s.surface, color: s.fg }}
        >
          <div className="flex items-center justify-between mb-3">
            <div>
              <p
                className="text-[10px] uppercase tracking-widest"
                style={{ color: s.fgMuted, fontFamily: s.monoFont }}
              >
                Today
              </p>
              <p className="text-sm font-bold" style={{ fontFamily: s.headingFont }}>
                Tuesday, 14 May
              </p>
            </div>
            <div className="flex gap-1.5">
              {[
                { l: "Revenue", v: "$2.4k" },
                { l: "Bookings", v: "18" },
              ].map((kpi) => (
                <div
                  key={kpi.l}
                  className="px-2 py-1.5 text-center"
                  style={{
                    background: onDark
                      ? "rgba(255,255,255,0.05)"
                      : s.surfaceWarm,
                    borderRadius: s.radius,
                  }}
                >
                  <p
                    className="text-[8px] uppercase tracking-wider"
                    style={{ color: s.fgMuted, fontFamily: s.monoFont }}
                  >
                    {kpi.l}
                  </p>
                  <p className="text-xs font-bold" style={{ fontFamily: s.headingFont }}>
                    {kpi.v}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* calendar columns */}
          <div className="grid grid-cols-3 gap-1.5">
            {["Amara", "Jordan", "Priya"].map((staff, ci) => (
              <div key={staff}>
                <div className="flex items-center gap-1 mb-1.5">
                  <div
                    className="w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold"
                    style={{
                      background:
                        ci === 0 ? s.accent : ci === 1 ? "#60A5FA" : "#F472B6",
                      color: "#0B0B0B",
                    }}
                  >
                    {staff[0]}
                  </div>
                  <p
                    className="text-[10px] font-semibold"
                    style={{ color: s.fg }}
                  >
                    {staff}
                  </p>
                </div>
                <div className="space-y-1">
                  {[
                    { t: "9:30", c: "Sophie R.", svc: "Balayage" },
                    { t: "12:00", c: "Mia T.", svc: "Cut & Style" },
                  ].map((a, i) => (
                    <div
                      key={a.t + ci}
                      className="px-2 py-1.5"
                      style={{
                        background:
                          ci === 0 && i === 0
                            ? `${s.accent}25`
                            : onDark
                              ? "rgba(255,255,255,0.05)"
                              : s.surfaceWarm,
                        borderLeft: `2px solid ${ci === 0 ? s.accent : ci === 1 ? "#60A5FA" : "#F472B6"}`,
                        borderRadius: s.radius,
                      }}
                    >
                      <p
                        className="text-[8px]"
                        style={{ color: s.fgMuted, fontFamily: s.monoFont }}
                      >
                        {a.t}
                      </p>
                      <p
                        className="text-[10px] font-semibold leading-tight"
                        style={{ color: s.fg }}
                      >
                        {a.c}
                      </p>
                      <p className="text-[9px]" style={{ color: s.fgMuted }}>
                        {a.svc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const HeroPreview = ({ s }) => {
  const onDark = s.heroOnDark || s.id === "iridescent-noir";
  const heroFg = onDark ? "#FFFFFF" : s.fg;
  const heroFgMuted = onDark ? "rgba(255,255,255,0.78)" : s.fgMuted;
  return (
    <div className="relative" style={{ background: s.heroBg }}>
      {s.heroOverlay && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: s.heroOverlay }}
        />
      )}
      {/* nav */}
      <div
        className="relative flex items-center justify-between px-6 py-4"
        style={{
          borderBottom: onDark
            ? "1px solid rgba(255,255,255,0.08)"
            : `1px solid ${s.border}`,
        }}
      >
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 flex items-center justify-center"
            style={{
              background: onDark ? "rgba(255,255,255,0.1)" : s.primary,
              color: onDark ? "#FFF" : s.primaryFg,
              borderRadius: s.radius,
            }}
          >
            <Calendar className="w-4 h-4" />
          </div>
          <span
            className="font-bold text-lg tracking-tight"
            style={{ fontFamily: s.headingFont, color: heroFg }}
          >
            SlotBook
          </span>
        </div>
        <div
          className="hidden md:flex items-center gap-5 text-xs uppercase tracking-widest"
          style={{ color: heroFgMuted, fontFamily: s.monoFont }}
        >
          <span>Product</span>
          <span>Pricing</span>
          <span>FAQ</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5"
            style={{
              background: s.accent,
              color: s.accentFg,
              borderRadius: "999px",
              fontFamily: s.bodyFont,
            }}
          >
            Start free <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* hero content */}
      <div className="relative px-6 sm:px-10 pt-10 pb-14">
        <div className="grid lg:grid-cols-[1.05fr,1fr] gap-8 items-center">
          {/* left */}
          <div>
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em]"
              style={{
                background: onDark
                  ? "rgba(255,255,255,0.08)"
                  : `${s.accent}25`,
                color: onDark ? s.accent : s.fg,
                fontFamily: s.monoFont,
                borderRadius: "999px",
                border: onDark
                  ? "1px solid rgba(255,255,255,0.12)"
                  : "1px solid transparent",
              }}
            >
              <Sparkles className="w-3 h-3" />
              For salons across Australia
            </span>

            <h1
              className="mt-6 font-extrabold leading-[0.95] tracking-[-0.02em]"
              style={{
                fontFamily: s.headingFont,
                fontSize: "clamp(2.4rem, 4.4vw, 4rem)",
                color: heroFg,
              }}
            >
              Empty hours,
              <br />
              <span style={{ color: s.accent }}>booked solid.</span>
            </h1>

            <p
              className="mt-5 text-sm leading-relaxed max-w-md"
              style={{ color: heroFgMuted, fontFamily: s.bodyFont }}
            >
              The booking platform built in Australia for hair, beauty,
              makeup and barber studios. 24/7 self-serve booking, fewer
              no-shows, paid deposits.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <button
                className="px-5 py-3 text-sm font-semibold flex items-center gap-2"
                style={{
                  background: s.accent,
                  color: s.accentFg,
                  borderRadius: "999px",
                  boxShadow: `0 14px 36px -10px ${s.accent}88`,
                  fontFamily: s.bodyFont,
                }}
              >
                Start free in 60 seconds <ArrowRight className="w-4 h-4" />
              </button>
              <button
                className="px-5 py-3 text-sm font-semibold flex items-center gap-2 border"
                style={{
                  borderColor: onDark
                    ? "rgba(255,255,255,0.18)"
                    : s.border,
                  color: heroFg,
                  background: "transparent",
                  borderRadius: "999px",
                  fontFamily: s.bodyFont,
                }}
              >
                <PlayCircle className="w-4 h-4" /> Watch 90-sec demo
              </button>
            </div>

            <div
              className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[11px]"
              style={{ color: heroFgMuted, fontFamily: s.bodyFont }}
            >
              {["14-day free trial", "No credit card", "Cancel anytime", "AU-based support"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5">
                  <Check className="w-3 h-3" style={{ color: s.success }} />
                  {t}
                </span>
              ))}
            </div>
          </div>

          {/* right: phone + browser combo */}
          <div className="relative flex items-center justify-center lg:justify-end gap-[-30px] min-h-[320px]">
            <div className="relative flex items-center" style={{ marginRight: "-30px" }}>
              <PhoneTile s={s} />
            </div>
            <BrowserTile s={s} />
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------- Below-hero strips ---------- */

const OutcomesStrip = ({ s }) => (
  <div
    className="grid grid-cols-3 gap-4 px-6 sm:px-10 py-7 border-t border-b"
    style={{
      background: s.surfaceWarm,
      borderColor: s.border,
      color: s.fg,
    }}
  >
    {[
      { k: "+24%", v: "Rebookings" },
      { k: "−38%", v: "No-shows" },
      { k: "$18k", v: "Extra revenue / mo" },
    ].map((o) => (
      <div key={o.v} className="flex flex-col gap-0.5">
        <span
          className="font-extrabold text-2xl sm:text-3xl"
          style={{ color: s.fg, fontFamily: s.headingFont }}
        >
          {o.k}
        </span>
        <span
          className="text-[10px] uppercase tracking-[0.2em]"
          style={{ color: s.fgMuted, fontFamily: s.monoFont }}
        >
          {o.v}
        </span>
      </div>
    ))}
  </div>
);

const FeatureBento = ({ s }) => (
  <div className="px-6 sm:px-10 py-10" style={{ background: s.pageBg, color: s.fg }}>
    <p
      className="text-[10px] font-bold tracking-[0.22em] uppercase mb-2"
      style={{ color: s.fgMuted, fontFamily: s.monoFont }}
    >
      What's included
    </p>
    <h2
      className="font-extrabold text-2xl sm:text-3xl mb-6 tracking-tight"
      style={{ fontFamily: s.headingFont }}
    >
      Everything your studio needs.
    </h2>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {[
        { icon: Calendar, t: "Smart scheduling", d: "24/7 self-serve with live availability." },
        { icon: Users, t: "Multi-staff", d: "Per-stylist schedules, services and KPIs." },
        { icon: Clock, t: "Fewer no-shows", d: "Auto SMS reminders + deposits." },
        { icon: TrendingUp, t: "Business insights", d: "Revenue, rebooking and trend reports." },
      ].map((f) => (
        <div
          key={f.t}
          className="p-4 border"
          style={{
            background: s.surface,
            borderColor: s.border,
            borderRadius: s.radius,
          }}
        >
          <div
            className="w-9 h-9 flex items-center justify-center mb-3"
            style={{
              background: `${s.accent}25`,
              color: s.fg,
              borderRadius: s.radius,
            }}
          >
            <f.icon className="w-4 h-4" />
          </div>
          <p className="font-bold text-sm" style={{ fontFamily: s.headingFont }}>
            {f.t}
          </p>
          <p className="text-[11px] mt-1 leading-snug" style={{ color: s.fgMuted }}>
            {f.d}
          </p>
        </div>
      ))}
    </div>
  </div>
);

const PricingStrip = ({ s }) => (
  <div
    className="px-6 sm:px-10 py-10 border-t"
    style={{ borderColor: s.border, background: s.surfaceWarm, color: s.fg }}
  >
    <h2
      className="font-extrabold text-2xl sm:text-3xl mb-6 tracking-tight"
      style={{ fontFamily: s.headingFont }}
    >
      Honest, AUD pricing.
    </h2>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {[
        { name: "Solo", price: "29", desc: "Solo artists & barbers", popular: false },
        { name: "Studio", price: "59", desc: "Up to 5 staff", popular: true },
        { name: "Salon", price: "99", desc: "Unlimited staff", popular: false },
      ].map((p) => (
        <div
          key={p.name}
          className="p-5 relative"
          style={{
            background: s.surface,
            border: p.popular ? `2px solid ${s.accent}` : `1px solid ${s.border}`,
            borderRadius: s.radius,
            boxShadow: p.popular ? `0 20px 40px -16px ${s.accent}55` : "none",
            transform: p.popular ? "translateY(-6px)" : "none",
          }}
        >
          {p.popular && (
            <span
              className="absolute -top-2.5 left-5 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.2em]"
              style={{
                background: s.accent,
                color: s.accentFg,
                borderRadius: "999px",
                fontFamily: s.monoFont,
              }}
            >
              Most popular
            </span>
          )}
          <p className="text-sm font-semibold" style={{ fontFamily: s.headingFont }}>
            {p.name}
          </p>
          <p className="text-[10px]" style={{ color: s.fgMuted }}>
            {p.desc}
          </p>
          <div className="flex items-baseline gap-1 mt-3">
            <span
              className="text-3xl font-extrabold"
              style={{ fontFamily: s.headingFont }}
            >
              ${p.price}
            </span>
            <span className="text-[11px]" style={{ color: s.fgMuted, fontFamily: s.monoFont }}>
              AUD / mo · GST inc.
            </span>
          </div>
          <ul className="mt-3 space-y-1.5">
            {["Online booking", "SMS reminders", "Insights"].map((b) => (
              <li
                key={b}
                className="flex items-center gap-1.5 text-[11px]"
                style={{ color: s.fgMuted }}
              >
                <Check className="w-3 h-3" style={{ color: s.success }} />
                {b}
              </li>
            ))}
          </ul>
          <button
            className="mt-4 w-full py-2 text-xs font-bold"
            style={
              p.popular
                ? {
                    background: s.accent,
                    color: s.accentFg,
                    borderRadius: "999px",
                    fontFamily: s.bodyFont,
                  }
                : {
                    background: s.pageBg,
                    color: s.fg,
                    border: `1px solid ${s.border}`,
                    borderRadius: "999px",
                    fontFamily: s.bodyFont,
                  }
            }
          >
            Start free trial
          </button>
        </div>
      ))}
    </div>
  </div>
);

const TestimonialStrip = ({ s }) => (
  <div
    className="px-6 sm:px-10 py-8 border-t"
    style={{ borderColor: s.border, background: s.pageBg, color: s.fg }}
  >
    <div className="flex gap-3 items-start">
      <div
        className="w-10 h-10 flex items-center justify-center text-sm font-bold flex-shrink-0"
        style={{ background: s.accent, color: s.accentFg, borderRadius: "999px" }}
      >
        AB
      </div>
      <div>
        <div className="flex gap-0.5 mb-1" style={{ color: s.accent }}>
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="w-3 h-3 fill-current" />
          ))}
        </div>
        <p
          className="text-base leading-relaxed"
          style={{
            color: s.fg,
            fontFamily: s.headingFont,
            fontWeight: 500,
          }}
        >
          "We went from a chaotic paper diary to a fully booked calendar.
          No-shows dropped overnight."
        </p>
        <p className="text-[11px] mt-2" style={{ color: s.fgMuted, fontFamily: s.monoFont }}>
          AMARA BENNETT · OWNER, LUMIÈRE SALON · SYDNEY
        </p>
      </div>
    </div>
  </div>
);

const FinalCta = ({ s }) => (
  <div
    className="px-6 sm:px-10 py-10 text-center"
    style={{
      background: s.id === "iridescent-noir" ? "#0A0A12" : s.primary,
      color: s.primaryFg,
    }}
  >
    <h3
      className="font-extrabold text-2xl sm:text-3xl mb-2 tracking-tight"
      style={{ fontFamily: s.headingFont }}
    >
      Make empty hours impossible.
    </h3>
    <p
      className="text-xs opacity-80 mb-5"
      style={{ fontFamily: s.monoFont, letterSpacing: "0.1em" }}
    >
      14-day free trial · No credit card · Cancel anytime
    </p>
    <div className="flex flex-wrap justify-center gap-3">
      <button
        className="px-5 py-2.5 text-sm font-bold flex items-center gap-2"
        style={{
          background: s.accent,
          color: s.accentFg,
          borderRadius: "999px",
          fontFamily: s.bodyFont,
        }}
      >
        Start free trial <ArrowRight className="w-4 h-4" />
      </button>
      <button
        className="px-5 py-2.5 text-sm font-bold border"
        style={{
          borderColor: "rgba(255,255,255,0.4)",
          color: s.primaryFg,
          background: "transparent",
          borderRadius: "999px",
          fontFamily: s.bodyFont,
        }}
      >
        Book a demo
      </button>
    </div>
  </div>
);

const SchemeMockup = ({ s }) => (
  <div
    className="rounded-3xl overflow-hidden border shadow-2xl"
    data-testid={`mockup-${s.id}`}
    style={{
      borderColor:
        s.id === "iridescent-noir" ? "rgba(255,255,255,0.08)" : s.border,
      background: s.pageBg,
      color: s.fg,
    }}
  >
    <HeroPreview s={s} />
    <OutcomesStrip s={s} />
    <FeatureBento s={s} />
    <PricingStrip s={s} />
    <TestimonialStrip s={s} />
    <FinalCta s={s} />
  </div>
);

const SwatchRow = ({ s }) => (
  <div className="flex items-center gap-2 mt-4 flex-wrap">
    {[
      ["BG", s.pageBg],
      ["Surface", s.surface],
      ["Text", s.fg],
      ["Primary", s.primary],
      ["Accent", s.accent],
      ["Success", s.success],
    ].map(([label, color]) => (
      <div key={label} className="flex flex-col items-center gap-1">
        <div
          className="w-10 h-10 border"
          style={{
            background: color,
            borderColor: "rgba(0,0,0,0.08)",
            borderRadius: s.radius,
          }}
          title={`${label}: ${color}`}
        />
        <span className="text-[10px] font-mono uppercase tracking-wider opacity-60">
          {label}
        </span>
      </div>
    ))}
  </div>
);

export default function MockupsPreview() {
  return (
    <div
      className="min-h-screen py-10 px-4 sm:px-8"
      style={{ background: "#101015" }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <p
            className="text-[10px] uppercase tracking-[0.3em] font-bold mb-3"
            style={{
              color: "#A78BFA",
              fontFamily: '"JetBrains Mono",ui-monospace,monospace',
            }}
          >
            Design Direction · Round 2
          </p>
          <h1
            className="font-extrabold text-3xl sm:text-5xl mb-3 tracking-tight"
            style={{
              fontFamily:
                '"Cabinet Grotesk","Outfit",system-ui,sans-serif',
              color: "#F5F5F0",
            }}
          >
            3 bold visual directions for SlotBook
          </h1>
          <p
            className="max-w-2xl mx-auto text-sm sm:text-base"
            style={{ color: "rgba(245,245,240,0.6)" }}
          >
            Each tile is a rendered preview of the full hero (with the new
            phone + browser combo), outcomes strip, features, pricing,
            testimonial, and final CTA — in one cohesive visual direction.
            Tell me which to build, or which elements to combine.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-12">
          {SCHEMES.map((s) => (
            <div key={s.id} data-testid={`mockup-card-${s.id}`}>
              <div className="flex items-baseline justify-between mb-3 px-1 flex-wrap gap-2">
                <div>
                  <h2
                    className="font-bold text-2xl tracking-tight"
                    style={{
                      color: "#F5F5F0",
                      fontFamily:
                        '"Cabinet Grotesk","Outfit",system-ui,sans-serif',
                    }}
                  >
                    {s.name}
                  </h2>
                  <p
                    className="text-sm mt-1 max-w-xl"
                    style={{ color: "rgba(245,245,240,0.55)" }}
                  >
                    {s.blurb}
                  </p>
                  <p
                    className="text-[10px] uppercase tracking-[0.2em] mt-1.5"
                    style={{
                      color: "rgba(245,245,240,0.35)",
                      fontFamily: '"JetBrains Mono",ui-monospace,monospace',
                    }}
                  >
                    Reference · {s.benchmark}
                  </p>
                </div>
                <span
                  className="text-[10px] font-mono uppercase tracking-widest"
                  style={{
                    color: "rgba(245,245,240,0.4)",
                    fontFamily: '"JetBrains Mono",ui-monospace,monospace',
                  }}
                >
                  {s.id}
                </span>
              </div>
              <SchemeMockup s={s} />
              <div className="px-1">
                <SwatchRow s={s} />
              </div>
            </div>
          ))}
        </div>

        <div
          className="mt-16 p-6 sm:p-8 max-w-3xl mx-auto text-center"
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: "18px",
            color: "#F5F5F0",
          }}
        >
          <p
            className="text-[10px] uppercase tracking-[0.3em] font-bold mb-2"
            style={{ color: "#A78BFA", fontFamily: '"JetBrains Mono",monospace' }}
          >
            Next step
          </p>
          <h3
            className="font-bold text-xl mb-2"
            style={{
              fontFamily: '"Cabinet Grotesk","Outfit",system-ui,sans-serif',
            }}
          >
            Which direction should I build?
          </h3>
          <p className="text-sm" style={{ color: "rgba(245,245,240,0.7)" }}>
            Reply with{" "}
            <span
              className="font-mono px-1.5 py-0.5 rounded"
              style={{ background: "rgba(255,255,255,0.08)" }}
            >
              pangram-editorial
            </span>
            ,{" "}
            <span
              className="font-mono px-1.5 py-0.5 rounded"
              style={{ background: "rgba(255,255,255,0.08)" }}
            >
              iridescent-noir
            </span>
            , or{" "}
            <span
              className="font-mono px-1.5 py-0.5 rounded"
              style={{ background: "rgba(255,255,255,0.08)" }}
            >
              sunset-bondi
            </span>
            . Or mix & match — e.g. "Sunset hero, Pangram typography, Iridescent dashboard".
          </p>
        </div>
      </div>
    </div>
  );
}
